'use client'

import { useCallback } from 'react'
import { useMessage } from '@/provider/MessageProvider'
import useI18n from 'Hooks/useI18n'

export function CopyButton({ text }: { text: string }) {
  const { t } = useI18n()
  const messageApi = useMessage()

  const handleCopy = useCallback(async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text)
      } else {
        const textarea = document.createElement('textarea')
        textarea.value = text
        textarea.setAttribute('readonly', 'true')
        textarea.style.position = 'fixed'
        textarea.style.opacity = '0'
        document.body.appendChild(textarea)
        textarea.select()
        document.execCommand('copy')
        document.body.removeChild(textarea)
      }
      messageApi.success(t('Copied successfully.'))
    } catch {
      messageApi.error(t('Copy failed. Please try again.'))
    }
  }, [text, messageApi, t])

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="shrink-0 rounded-md border border-white/15 bg-white/[0.06] px-2.5 py-1 text-xs text-white/60 transition hover:bg-white/[0.1]"
    >
      {t('Copy')}
    </button>
  )
}

export function CodeBlock({ code }: { code: string }) {
  return (
    <div className="relative rounded-lg border border-white/10 bg-black/30 p-4">
      <div className="absolute right-3 top-3">
        <CopyButton text={code} />
      </div>
      <pre className="overflow-x-auto whitespace-pre-wrap pr-16 font-code text-[13px] leading-6 text-white/80">
        {code}
      </pre>
    </div>
  )
}

export function ScrollableBlock({ text }: { text: string }) {
  return (
    <div className="relative rounded-lg border border-white/10 bg-black/30 p-4">
      <div className="absolute right-3 top-3 z-10">
        <CopyButton text={text} />
      </div>
      <div className="max-h-[220px] overflow-y-auto pr-16">
        <pre className="whitespace-pre-wrap font-code text-[13px] leading-6 text-white/80">
          {text}
        </pre>
      </div>
    </div>
  )
}

export function InlineCode({ code }: { code: string }) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-white/10 bg-black/30 px-4 py-2.5">
      <code className="font-code text-[13px] text-white/80">{code}</code>
      <CopyButton text={code} />
    </div>
  )
}
